import { useLayoutEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * The wedding's budget, typed as dollars rather than as a number (EVE-68).
 *
 * It used to be `<input type="number">`, which is the honest control for a
 * quantity and the wrong one for money: `105000` has no comma to count, so the
 * extra zero that turns a hundred thousand into a million is invisible until
 * it is saved, and the spinner on a six-digit figure is a hit target for a
 * mistake nobody wants to make. A grouped figure is also simply how the number
 * is written down everywhere else in this product.
 *
 * Masking a controlled input costs one thing, and it is the caret: every
 * keystroke reformats the text under it, so a caret restored by character
 * position drifts a place to the right every time a separator appears, and
 * backspacing onto a comma deletes a character the mask immediately puts back,
 * which reads as a dead key. Both are handled by thinking in digits — the
 * caret is remembered as "after the Nth digit", and a keystroke that removed
 * only a separator is re-read as the deletion of the digit it was reaching
 * for.
 *
 * The value stays a string of bare digits, because that is what the dialogs
 * around it already hold and hand to `Number()` when they build the request;
 * the commas exist on screen and nowhere else.
 */

/** Past a trillion dollars this is a typo, and the digits stop being exact. */
const MAX_DIGITS = 12;

/** The dollars in anything a planner might type or paste: a symbol, grouping
 *  commas, spaces, and cents that this field does not keep. */
export function budgetDigits(text: string): string {
  const [dollars = ''] = text.split('.');
  return dollars
    .replace(/\D/g, '')
    .replace(/^0+(?=\d)/, '')
    .slice(0, MAX_DIGITS);
}

export function groupDigits(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export default function BudgetEstimateField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  /** Bare digits. `''` means "not set", as everywhere else in this form. */
  value: string;
  onChange: (digits: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  /** Where the caret belongs once React has re-rendered the grouped value,
   *  counted in digits before it. Null means "leave the caret alone". */
  const caretAfterDigits = useRef<number | null>(null);
  /** Which key produced the change being read, since the browser reports the
   *  same resulting text for a backspace and a forward delete around a comma. */
  const lastKey = useRef<string | null>(null);

  const display = groupDigits(budgetDigits(value));

  // Runs on every render: the caret has to be placed after React has written
  // the grouped text, and the ref it depends on cannot be a dependency.
  useLayoutEffect(() => {
    const el = inputRef.current;
    const target = caretAfterDigits.current;
    if (!el || target === null) return;
    caretAfterDigits.current = null;

    let seen = 0;
    let pos = 0;
    while (pos < el.value.length && seen < target) {
      if (/\d/.test(el.value.charAt(pos))) seen++;
      pos++;
    }
    el.setSelectionRange(pos, pos);
  });

  function handleChange(typed: string, caret: number) {
    let digits = budgetDigits(typed);
    let afterDigits = Math.min(typed.slice(0, caret).replace(/\D/g, '').length, digits.length);

    // The text shrank but the digits did not, so what the keystroke actually
    // removed was a separator — and nobody presses a key at a comma meaning to
    // delete the comma.
    if (typed.length < display.length && digits === budgetDigits(display)) {
      const victim = lastKey.current === 'Delete' ? afterDigits : afterDigits - 1;
      if (victim >= 0 && victim < digits.length) {
        digits = budgetDigits(digits.slice(0, victim) + digits.slice(victim + 1));
        // Either way the caret ends up where the removed digit was.
        afterDigits = Math.min(victim, digits.length);
      }
    }

    caretAfterDigits.current = afterDigits;
    lastKey.current = null;
    onChange(digits);
  }

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {/* The symbol is drawn beside the digits rather than masked into them,
          so the caret never has to be kept out of it. */}
      <div className="relative">
        <span
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2
            text-base text-muted-foreground md:text-sm"
        >
          $
        </span>
        <Input
          id={id}
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="45,000"
          value={display}
          onKeyDown={(e) => {
            lastKey.current = e.key;
          }}
          onChange={(e) => handleChange(e.target.value, e.target.selectionStart ?? e.target.value.length)}
          /* Tabular figures so the digits do not shuffle sideways as a comma
             appears under the caret. */
          className="pl-7 tabular-nums"
        />
      </div>
    </div>
  );
}
